/**
 * @file
 * Dismisses server-rendered and Drupal Message API notifications.
 */
(function (Drupal, once) {
  'use strict';

  const timers = new Map();
  // Observe removals made by the Message API as well as AJAX replacements.
  const observer = new MutationObserver(() => {
    timers.forEach((state, element) => {
      if (element.isConnected) state.update();
      else state.cleanup();
    });
  });

  const addTimer = (element, dismiss) => {
    // Errors and messages requiring input remain until manually dismissed.
    // Use data-message-persistent for required actions presented as links.
    if (element.classList.contains('messages--error')
      || element.matches('[data-message-persistent]')
      || element.querySelector('form, input, select, textarea, button:not(.messages__dismiss), [data-message-persistent]')) return;

    const controller = new AbortController();
    let timeout;
    let startedAt;
    let remaining;
    const state = {
      mounted: false,
      cleanup() {
        clearTimeout(timeout);
        controller.abort();
        timers.delete(element);
        if (!timers.size) observer.disconnect();
      },
      update() {
        if (!element.isConnected) return;
        if (!state.mounted) {
          state.mounted = true;
          const content = element.cloneNode(true);
          content.querySelectorAll('.messages__dismiss, .visually-hidden, [hidden], [aria-hidden="true"]').forEach(node => node.remove());
          const words = content.textContent.trim().split(/\s+/u).filter(Boolean).length;
          // Reading time: at least 5 seconds; otherwise 2 seconds to orient
          // plus 0.4 seconds per word (150 words/minute). No upper cap.
          remaining = Math.max(5000, 2000 + words * 400);
        }
        const paused = document.hidden || element.matches(':hover')
          || element.contains(document.activeElement);
        if (paused && timeout !== undefined) {
          remaining = Math.max(0, remaining - (performance.now() - startedAt));
          clearTimeout(timeout);
          timeout = undefined;
        }
        else if (!paused && timeout === undefined) {
          startedAt = performance.now();
          timeout = setTimeout(() => {
            state.cleanup();
            dismiss();
          }, remaining);
        }
      },
    };
    timers.set(element, state);
    observer.observe(document.documentElement, { childList: true, subtree: true });
    ['mouseenter', 'mouseleave', 'focusin'].forEach(event => {
      element.addEventListener(event, state.update, { signal: controller.signal });
    });
    element.addEventListener('focusout', () => queueMicrotask(() => {
      if (!controller.signal.aborted) state.update();
    }), { signal: controller.signal });
    document.addEventListener('visibilitychange', state.update, { signal: controller.signal });
    state.update();
  };

  const addDismissButton = (message) => {
    once('adocs-message-dismiss', message).forEach((element) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'messages__dismiss';
      const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      icon.setAttribute('aria-hidden', 'true');
      icon.setAttribute('focusable', 'false');
      const use = document.createElementNS('http://www.w3.org/2000/svg', 'use');
      use.setAttribute('href', element.querySelector('.messages__content--add-to-cart')
        ? '#zoom_plus' : '#closing_cross');
      icon.append(use);
      button.append(icon);
      button.setAttribute('aria-label', Drupal.t('Close'));
      const dismiss = () => {
        timers.get(element)?.cleanup();
        const restoreFocus = element.contains(document.activeElement);
        const wrapper = element.closest('[data-drupal-messages]');
        element.remove();
        if (restoreFocus) {
          const target = wrapper?.querySelector('.messages__dismiss')
            || document.getElementById('main-content');
          target?.focus({ preventScroll: true });
        }
      };
      button.addEventListener('click', dismiss);
      element.append(button);
      addTimer(element, dismiss);
    });
    return message;
  };

  // Message API additions do not necessarily reattach Drupal behaviors.
  const themeMessage = Drupal.theme.message;
  Drupal.theme.message = (message, options) =>
    addDismissButton(themeMessage(message, options));

  Drupal.behaviors.adocsMessages = {
    attach(context) {
      if (context.matches?.('.messages')) {
        addDismissButton(context);
      }
      context.querySelectorAll('.messages').forEach(addDismissButton);
    },
    detach(context, settings, trigger) {
      if (trigger !== 'unload') return;
      timers.forEach((state, element) => {
        if (element === context || context.contains(element)) state.cleanup();
      });
    },
  };
})(Drupal, once);
